import { useAtom } from 'jotai'
import { useToast } from '@/hooks/use-toast'
import { addChartAtom, createStandardChartObject } from '../store/chartAtoms'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../config/firebase'
import { organizationService } from '../services/organizationService'
import { DataSource } from '@shared'

interface QuickActionTemplate {
  id: string
  title: string
  description: string
  chartType: 'line' | 'bar' | 'pie'
  dataSourceType: 'google-analytics' | 'firestore' | 'any'
  metrics: string[]
  dimensions: string[]
  dateRange: string
  badge: string
}

export const useQuickChartCreation = () => {
  const [, addChart] = useAtom(addChartAtom)
  const { toast } = useToast()

  const createChartFromTemplate = async (
    template: QuickActionTemplate, 
    dataSources: DataSource[]
  ) => {
    try {
      const connectedSources = dataSources.filter(ds => 
        ds.status === 'connected' || ds.status === 'active'
      )
      
      const targetSources = connectedSources.filter(ds => 
        template.dataSourceType === 'any' || ds.type === template.dataSourceType
      )

      if (targetSources.length === 0) {
        // Generate sample data for templates without connected sources
        const sampleData = generateSampleDataForTemplate(template)
        
        const newChart = createStandardChartObject({
          data: sampleData.data,
          columns: Object.keys(sampleData.data[0] || {}),
          chartType: template.chartType,
          xAxis: sampleData.xAxis,
          yAxis: sampleData.yAxis,
          title: `${template.title} (Sample Data)`,
        })
        
        addChart(newChart)
        
        toast({
          title: "Chart Created! 🎉",
          description: `${template.title} created with sample data. Connect data sources for real data.`,
        })
        
        return
      }

      // Use real data from connected sources
      const selectedSource = targetSources[0] // Use first available source
      
      console.log('🚀 Creating chart from template:', template.title)
      console.log('🚀 Using data source:', selectedSource.name)
      
      const payload = {
        organizationId: organizationService.getCurrentOrganizationId(),
        intent: `Show me ${template.title.toLowerCase()}`,
        parsedIntent: {
          metrics: template.metrics,
          dimensions: template.dimensions,
          dateRange: template.dateRange,
          chartType: template.chartType,
        },
        availableDataSources: [selectedSource.id]
      }
      
      const generateChartFunction = httpsCallable(functions, 'generateChartFromIntent')
      const result = await generateChartFunction(payload)
      const resultData = result.data as any
      
      if (resultData.success && resultData.chartData) {
        const chartData = resultData.chartData
        
        const newChart = createStandardChartObject({
          data: chartData.data,
          columns: Object.keys(chartData.data[0] || {}),
          chartType: template.chartType,
          xAxis: chartData.xAxis,
          yAxis: chartData.yAxis,
          title: template.title,
        })
        
        addChart(newChart)
        
        toast({
          title: "Chart Created! 🎉",
          description: `${template.title} created from ${selectedSource.name}.`,
        })
      } else {
        throw new Error(resultData.message || 'Chart creation failed')
      }
      
    } catch (error: any) {
      console.error('Error creating chart from template:', error)
      
      toast({
        title: "Error Creating Chart",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      })
    }
  }

  return { createChartFromTemplate }
}

// Sample data generators for different template types
const generateSampleDataForTemplate = (template: QuickActionTemplate) => {
  switch (template.id) {
    case 'website-traffic':
      return {
        data: [
          { date: '2024-12-01', activeUsers: 1250 },
          { date: '2024-12-02', activeUsers: 1340 },
          { date: '2024-12-03', activeUsers: 1180 },
          { date: '2024-12-04', activeUsers: 1420 },
          { date: '2024-12-05', activeUsers: 1380 },
          { date: '2024-12-06', activeUsers: 1560 },
          { date: '2024-12-07', activeUsers: 1680 },
          { date: '2024-12-08', activeUsers: 1520 },
          { date: '2024-12-09', activeUsers: 1780 },
          { date: '2024-12-10', activeUsers: 1690 }
        ],
        xAxis: 'date',
        yAxis: 'activeUsers'
      }
    
    case 'device-breakdown':
      return {
        data: [
          { deviceCategory: 'Desktop', activeUsers: 3240 },
          { deviceCategory: 'Mobile', activeUsers: 5680 },
          { deviceCategory: 'Tablet', activeUsers: 1230 }
        ],
        xAxis: 'deviceCategory',
        yAxis: 'activeUsers'
      }
    
    case 'top-pages':
      return {
        data: [
          { pagePath: '/home', screenPageViews: 12500 },
          { pagePath: '/products', screenPageViews: 8900 },
          { pagePath: '/about', screenPageViews: 4300 },
          { pagePath: '/contact', screenPageViews: 2100 },
          { pagePath: '/blog', screenPageViews: 6700 }
        ],
        xAxis: 'pagePath',
        yAxis: 'screenPageViews'
      }
    
    case 'user-signups':
      return {
        data: [
          { createdAt: '2024-12-01', count: 34 },
          { createdAt: '2024-12-02', count: 42 },
          { createdAt: '2024-12-03', count: 38 },
          { createdAt: '2024-12-04', count: 56 },
          { createdAt: '2024-12-05', count: 49 },
          { createdAt: '2024-12-06', count: 63 },
          { createdAt: '2024-12-07', count: 71 }
        ],
        xAxis: 'createdAt',
        yAxis: 'count'
      }
    
    case 'collection-counts':
      return {
        data: [
          { collection: 'users', documentCount: 1420 },
          { collection: 'posts', documentCount: 3280 },
          { collection: 'comments', documentCount: 8900 },
          { collection: 'likes', documentCount: 12400 },
          { collection: 'sessions', documentCount: 5600 }
        ],
        xAxis: 'collection',
        yAxis: 'documentCount'
      }
    
    default:
      return {
        data: [
          { category: 'A', value: 100 },
          { category: 'B', value: 200 },
          { category: 'C', value: 150 }
        ],
        xAxis: 'category',
        yAxis: 'value'
      }
  }
} 